import React, { createContext, useCallback, useContext, useState } from 'react';

interface StepContextData {
  currentStep?: number;
  currentStepName?: string;
  completedSteps: string[];
  setStepContext: (step: number, stepName: string, completed: string[]) => void;
}

const StepContext = createContext<StepContextData>({
  completedSteps: [],
  setStepContext: () => {},
});

export const useStepContext = () => useContext(StepContext);

export const StepContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentStep, setCurrentStep] = useState<number>();
  const [currentStepName, setCurrentStepName] = useState<string>();
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  // R20 fix: wrap in useCallback so the function reference is stable across renders.
  // Without useCallback, setStepContext is a new function on every StepContextProvider render.
  // ApplicationShell's useEffect([setStepContext, ...]) would then fire on every render,
  // calling setCompletedSteps([...new array...]) which triggers another render → infinite loop.
  // React state setters (setCurrentStep etc.) are guaranteed stable, so [] deps is correct.
  const setStepContext = useCallback((step: number, stepName: string, completed: string[]) => {
    setCurrentStep(step);
    setCurrentStepName(stepName);
    // Use functional update with length+content equality check to avoid re-renders
    // when the completed steps list hasn't actually changed (same contents, new array ref).
    setCompletedSteps((prev) => {
      if (prev.length === completed.length && prev.every((s, i) => s === completed[i])) return prev;
      return completed;
    });
  }, []);

  return (
    <StepContext.Provider
      value={{
        currentStep,
        currentStepName,
        completedSteps,
        setStepContext,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};